import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  Paper,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
} from "@mui/material";
import {
  BusinessCenter as BusinessCenterIcon,
  StarBorder as StarBorderIcon,
  Folder as FolderIcon,
  Add as AddIcon,
  DeleteOutlined as DeleteOutlineIcon,
  LockOutlined as LockOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { showErrorToast } from "utils/toastNotification";

import { fetchProjects } from "store/features/company/projectManagement/project/projectActions";
import {
  selectProjects,
  selectProjectLoading,
} from "store/features/company/projectManagement/project/projectSelectors";

import { fetchDepartments } from "store/features/company/department/departmentActions";
import {
  selectDepartments,
  selectDepartmentLoading,
} from "store/features/company/department/departmentSelectors";

import {
  createNewRequisition,
  fetchRequisitions,
  fetchRequisitionMasterItems,
} from "store/features/company/requisition/requisitionActions";
import { selectRequisitions } from "store/features/company/requisition/requisitionSelectors";

import { selectCurrentUser } from "store/features/auth/authSelectors";
import usePermissions from "hooks/usePermissions";
import addRequisitionSvg from "assets/modals/add.svg";
import { getSecureLocalItem } from "utils/secureSessionStorage";

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtINR = (n) =>
  "₹ " +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const genReqNo = () =>
  "REQ-" +
  new Date().getFullYear() +
  "-" +
  (Math.floor(Math.random() * 9000) + 1000);

const todayISO = () => new Date().toISOString().split("T")[0];

const EMPTY_ROW = () => ({
  id: Date.now() + Math.random(),
  itemName: "",
  description: "",
  qty: "",
  rate: "",
  requiredBy: "",
});

// ── Section Card Component ────────────────────────────────────────────────────
const SectionCard = ({ title, children, sx = {} }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const surface = isDark ? "rgba(30,41,59,0.6)" : "rgba(255,255,255,0.75)";
  const cardBorder = isDark ? "#1e293b" : "#f3f4f6";
  const textSecondary = isDark ? "#9ca3af" : "#6b7280";

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, sm: 3.5 },
        borderRadius: "14px",
        backgroundColor: surface,
        border: `1px solid ${cardBorder}`,
        backdropFilter: "blur(10px)",
        mb: 3,
        ...sx,
      }}
    >
      {title && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 2.5 }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.7px",
              color: textSecondary,
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          <Divider sx={{ flex: 1, borderColor: cardBorder }} />
        </Stack>
      )}
      {children}
    </Paper>
  );
};

// ── Table Row Component with Local State ──────────────────────────────────────

const TableRowItem = ({
  row,
  idx,
  cardBorder,
  isDark,
  textSecondary,
  textPrimary,
  onRowUpdate,
  deleteRow,
  tblInputSx,
  masterItems,
  masterItemsLoading,
  canDeleteRow = true,
}) => {
  const [localRow, setLocalRow] = useState(row);

  const handleFieldChange = (field, value) => {
    const updatedRow = { ...localRow, [field]: value };
    setLocalRow(updatedRow);
    onRowUpdate(row.id, updatedRow);
  };

  const amt =
    (parseFloat(localRow.qty) || 0) * (parseFloat(localRow.rate) || 0);
  const hasAmt = amt > 0;
  const handleItemSelect = (event, newValue) => {
    // If newValue is an object (selected from dropdown)
    if (newValue && typeof newValue === "object") {
      const updatedRow = {
        ...localRow,
        itemName: newValue.item_name,
        description: newValue.item_description,
      };
      setLocalRow(updatedRow);
      onRowUpdate(row.id, updatedRow);
    }
    // If newValue is a string (typed custom value)
    else if (typeof newValue === "string") {
      const updatedRow = {
        ...localRow,
        itemName: newValue,
        description: localRow.description || "",
      };
      setLocalRow(updatedRow);
      onRowUpdate(row.id, updatedRow);
    }
    // If cleared
    else {
      const updatedRow = {
        ...localRow,
        itemName: "",
        description: "",
      };
      setLocalRow(updatedRow);
      onRowUpdate(row.id, updatedRow);
    }
  };
  return (
    <TableRow
      sx={{
        borderBottom: `1px solid ${cardBorder}`,
        "&:last-child": { borderBottom: "none" },
        "&:hover": {
          backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#f9fafb",
        },
        transition: "background 0.1s",
      }}
    >
      {/* # */}
      <TableCell
        align="center"
        sx={{
          color: textSecondary,
          fontSize: "12px",
          fontWeight: 600,
          px: 1,
          borderBottom: "none",
        }}
      >
        {idx + 1}
      </TableCell>

      {/* Item Name with dropdown - using Autocomplete */}
      <TableCell
        sx={{ px: 0.75, py: 0.75, minWidth: 160, borderBottom: "none" }}
      >
        <Autocomplete
          freeSolo
          fullWidth
          size="small"
          options={masterItems}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return option?.item_name || "";
          }}
          loading={masterItemsLoading}
          value={localRow.itemName || null}
          onChange={handleItemSelect}
          onInputChange={(event, newInputValue) => {
            // Update itemName as user types
            if (!event || event.type !== "blur") {
              const updatedRow = {
                ...localRow,
                itemName: newInputValue,
              };
              setLocalRow(updatedRow);
              onRowUpdate(row.id, updatedRow);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search or type item"
              sx={tblInputSx}
              InputProps={{
                ...params.InputProps,
                sx: { fontSize: "13px" },
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {option.item_name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {option.item_description}
                </Typography>
              </Box>
            </li>
          )}
          isOptionEqualToValue={(option, value) => {
            if (!value) return false;
            if (typeof value === "string") return option.item_name === value;
            return option.id === value.id;
          }}
        />
      </TableCell>

      {/* Description - auto-filled or editable */}
      <TableCell
        sx={{ px: 0.75, py: 0.75, minWidth: 200, borderBottom: "none" }}
      >
        <TextField
          fullWidth
          size="small"
          value={localRow.description}
          onChange={(e) => handleFieldChange("description", e.target.value)}
          placeholder="Item / service description"
          sx={tblInputSx}
          autoComplete="off"
        />
      </TableCell>

      {/* Qty */}
      <TableCell sx={{ px: 0.75, py: 0.75, width: 90, borderBottom: "none" }}>
        <TextField
          size="small"
          type="number"
          value={localRow.qty}
          onChange={(e) => handleFieldChange("qty", e.target.value)}
          inputProps={{
            min: 0,
            style: { textAlign: "right", fontFamily: "monospace" },
          }}
          placeholder="0"
          sx={tblInputSx}
        />
      </TableCell>

      {/* Rate */}
      <TableCell sx={{ px: 0.75, py: 0.75, width: 110, borderBottom: "none" }}>
        <TextField
          size="small"
          type="number"
          value={localRow.rate}
          onChange={(e) => handleFieldChange("rate", e.target.value)}
          inputProps={{
            min: 0,
            step: "0.01",
            style: { textAlign: "right", fontFamily: "monospace" },
          }}
          placeholder="0.00"
          sx={tblInputSx}
        />
      </TableCell>

      {/* Amount (computed) */}
      <TableCell
        align="right"
        sx={{
          fontFamily: "monospace",
          fontSize: "13px",
          fontWeight: 500,
          color: hasAmt ? textPrimary : textSecondary,
          px: 1.5,
          width: 110,
          whiteSpace: "nowrap",
          borderBottom: "none",
        }}
      >
        {hasAmt ? fmtINR(amt) : "—"}
      </TableCell>

      {/* Required By date */}
      <TableCell
        sx={{ px: 0.75, py: 0.75, minWidth: 130, borderBottom: "none" }}
      >
        <TextField
          size="small"
          type="date"
          value={localRow.requiredBy}
          onChange={(e) => handleFieldChange("requiredBy", e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={tblInputSx}
        />
      </TableCell>

      {/* Delete */}
      <TableCell
        align="center"
        sx={{ px: 0.5, width: 80, borderBottom: "none" }}
      >
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <IconButton
            size="small"
            type="button"
            onClick={() => { }}
            sx={{
              color: textSecondary,
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: isDark ? "#334155" : "#f1f5f9",
                color: textPrimary,
              },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
          <IconButton
            size="small"
            type="button"
            disabled={!canDeleteRow}
            onClick={() => deleteRow(row.id)}
            sx={{
              color: textSecondary,
              borderRadius: "6px",
              "&:hover": { backgroundColor: "#fef2f2", color: "#ef4444" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const CreateRequisition = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  // ── Redux ──────────────────────────────────────────────────────────────────

  const projects = useSelector(selectProjects);
  const projectLoading = useSelector(selectProjectLoading);
  const departments = useSelector(selectDepartments);
  const departmentLoading = useSelector(selectDepartmentLoading);
  const requisitions = useSelector(selectRequisitions);
  const currentUser = useSelector(selectCurrentUser);
  const { hasPermission } = usePermissions();

  const userRole = currentUser?.role || currentUser?.user_type;
  const canCreate = hasPermission("requisition.create") || userRole === "Company";
  const canDeleteRow = hasPermission("requisition.delete") || hasPermission("requisitions.delete") || hasPermission("requisition.create") || userRole === "Company";

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [purposeType, setPurposeType] = useState("COMPANY"); // "COMPANY" | "PROJECT"

  const [meta, setMeta] = useState({
    requested_by: "",
    date: todayISO(),
    req_no: genReqNo(),
    department: "",
    project_id: "",
    custom_project: "",
    description: "",
  });

  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [masterItems, setMasterItems] = useState([]);
  const [masterItemsLoading, setMasterItemsLoading] = useState(false);

  const steps = [
    { label: "Request Details", desc: "Enter basic requisition information" },
    { label: "Line Items", desc: "Add items to the requisition" },
    { label: "Review & Submit", desc: "Review and submit requisition" },
  ];

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (projects.length === 0) dispatch(fetchProjects());
    if (departments.length === 0) dispatch(fetchDepartments());
    dispatch(fetchRequisitions());
  }, [dispatch, projects.length, departments.length]);

  useEffect(() => {
    const empId = currentUser?.employee_id || currentUser?.id;
    if (empId) {
      setMeta((prev) => ({ ...prev, requested_by: empId }));
    }
  }, [currentUser]);

  // Add this useEffect after your existing effects
  useEffect(() => {
    const fetchMasterItems = async () => {
      setMasterItemsLoading(true);
      try {
        let projectId = null;
        if (
          purposeType === "PROJECT" &&
          meta.project_id &&
          meta.project_id !== "OTHER"
        ) {
          projectId = meta.project_id;
        }
        const result = await dispatch(fetchRequisitionMasterItems(projectId));
        if (result.success) {
          setMasterItems(result.data);
        }
      } catch (error) {
        console.error("Error fetching master items:", error);
      } finally {
        setMasterItemsLoading(false);
      }
    };

    fetchMasterItems();
  }, [purposeType, meta.project_id, dispatch]);

  // ── Meta handlers ──────────────────────────────────────────────────────────
  const handleMeta = (e) => {
    const { name, value } = e.target;
    setMeta((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ── Row handlers ───────────────────────────────────────────────────────────
  const handleRowUpdate = (id, updatedRow) => {
    setRows((prev) => prev.map((r) => (r.id === id ? updatedRow : r)));
  };

  const addRow = () => setRows((prev) => [...prev, EMPTY_ROW()]);

  const deleteRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  // ── Totals ─────────────────────────────────────────────────────────────────
  const totals = rows.reduce(
    (acc, r) => {
      const qty = parseFloat(r.qty) || 0;
      const rate = parseFloat(r.rate) || 0;
      return {
        items: acc.items + (qty > 0 || rate > 0 ? 1 : 0),
        qty: acc.qty + qty,
        amount: acc.amount + qty * rate,
      };
    },
    { items: 0, qty: 0, amount: 0 },
  );

  // ── Budget Checking Logic ──────────────────────────────────────────────────
  const selectedProject = projects.find(
    (p) => String(p.id) === String(meta.project_id),
  );
  const projectBudget = selectedProject
    ? parseFloat(selectedProject.budget || 0)
    : 0;

  const previousSpendings = requisitions
    .filter(
      (r) =>
        String(r.project_id) === String(meta.project_id) &&
        r.status === "APPROVED",
    )
    .reduce((sum, r) => sum + parseFloat(r.total_amount || 0), 0);

  const remainingBudget = projectBudget - previousSpendings;
  const isBudgetExceeded = remainingBudget - totals.amount < 0;

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateStep1 = () => {
    const e = {};
    if (!meta.department) e.department = "Department is required";
    if (!meta.description || !meta.description.trim())
      e.description = "Description is required";
    if (purposeType === "PROJECT" && !meta.project_id)
      e.project_id = "Project is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    const hasItem = rows.some(
      (r) =>
        r.itemName &&
        r.description &&
        parseFloat(r.qty) > 0 &&
        parseFloat(r.rate) > 0,
    );
    if (!hasItem) e.rows = "Add at least one complete line item";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Stepper Navigation ─────────────────────────────────────────────────────
  const handleNext = () => {
    if (activeStep === 0) {
      if (!validateStep1()) return;
    }
    if (activeStep === 1) {
      if (!validateStep2()) return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (activeStep !== 2) {
      handleNext();
      return;
    }
    try {
      setLoading(true);

      let finalRequestedBy = parseInt(meta.requested_by) || null;

      const userStr = getSecureLocalItem("user");
      if (userStr) {
        try {
          const userObj = JSON.parse(userStr);
          const candidateId = userObj?.employee_id || userObj?.id;
          if (candidateId) {
            finalRequestedBy = parseInt(candidateId) || null;
          }
        } catch (e) { }
      }


      const payload = {
        requisition_type: purposeType.toUpperCase(),
        department_id: parseInt(meta.department),
        requested_by: finalRequestedBy,
        description: meta.description,
        requested_at: meta.date,
        items: rows
          .filter(
            (r) =>
              r.itemName &&
              r.description &&
              parseFloat(r.qty) > 0 &&
              parseFloat(r.rate) > 0,
          )
          .map(({ itemName, description, qty, rate, requiredBy }) => ({
            item_name: itemName,
            item_description: description,
            quantity: parseFloat(qty),
            unit_rate: parseFloat(rate),
            required_by: requiredBy,
          })),
      };

      if (purposeType === "PROJECT") {
        payload.project_id = parseInt(meta.project_id);
      }

      const result = await dispatch(createNewRequisition(payload));
      if (result?.success) {
        resetForm();
      }
    } catch (err) {
      console.error("Error creating requisition:", err);
      showErrorToast("Something went wrong while creating the requisition");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPurposeType("COMPANY");
    setMeta({
      requested_by: "",
      date: todayISO(),
      req_no: genReqNo(),
      department: "",
      project_id: "",
      custom_project: "",
      description: "",
    });
    setRows([]);
    setErrors({});
    setActiveStep(0);
  };

  // ── Theme tokens ───────────────────────────────────────────────────────────
  const amber = "#f59e0b";
  const amberDark = "#d97706";
  const amberDeep = "#b45309";
  const cardBorder = isDark ? "#1e293b" : "#f3f4f6";
  const textPrimary = isDark ? "#ffffff" : "#111827";
  const textSecondary = isDark ? "#9ca3af" : "#6b7280";
  const inputBg = isDark ? "#1e293b" : "#f9fafb";
  const borderColor = isDark ? "#475569" : "#d1d5db";

  // Shared sx for all text fields
  const fieldSx = (hasError = false) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: inputBg,
      "& fieldset": { borderColor: hasError ? "#ef4444" : borderColor },
      "&:hover fieldset": { borderColor: hasError ? "#ef4444" : amber },
      "&.Mui-focused fieldset": {
        borderColor: hasError ? "#ef4444" : amber,
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: hasError ? "#ef4444" : textSecondary,
      "&.Mui-focused": { color: hasError ? "#ef4444" : amber },
    },
    "& .MuiInputLabel-asterisk": { color: "#ef4444" },
  });

  // Table cell input sx
  const tblInputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "transparent",
      fontSize: "13px",
      "& fieldset": { borderColor: borderColor },
      "&:hover fieldset": { borderColor: amber },
      "&.Mui-focused fieldset": { borderColor: amber, borderWidth: "2px" },
      "& input": { padding: "7px 10px" },
    },
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (!canCreate) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: "center",
            borderRadius: "16px",
            border: `1px solid ${isDark ? "#1e293b" : "#e5e7eb"}`,
            backgroundColor: isDark ? "rgba(15,23,42,0.6)" : "#ffffff",
          }}
        >
          <Typography variant="h6" fontWeight={700} color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You do not have permission to create requisitions.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background: isDark
        //   ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //   : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: { xs: 1, sm: 2 },
      }}
    >
      <Box>
        {/* ── Page Header & Stepper ────────────────────────────────────────── */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
          <Box
            component="img"
            src={addRequisitionSvg}
            alt="Add Requisition"
            sx={{ width: 60, height: 60, objectFit: "contain" }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, color: textPrimary, mb: 0.5 }}
            >
              Create New Requisition
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {activeStep === 0
                ? "Enter basic requisition information"
                : activeStep === 1
                  ? "Fill in the details below and add line items to submit"
                  : "Review all the details before submitting the requisition"}
            </Typography>
          </Box>
        </Stack>

        {/* ── Custom Stepper ───────────────────────────────────────────────── */}
        <Box sx={{ width: "80%", margin: "0 auto", mb: 6 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepConnector-line": {
                borderColor: isDark ? "#334155" : "#cbd5e1",
                borderTopWidth: 2,
              },
              "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line": {
                borderColor: amber,
              },
              "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
                borderColor: amber,
              },
            }}
          >
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={(props) => {
                    const { active, completed, icon } = props;
                    return (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor:
                            active || completed
                              ? amber
                              : isDark
                                ? "#1e293b"
                                : "#f1f5f9",
                          color: active || completed ? "#fff" : textSecondary,
                          fontWeight: 700,
                          fontSize: "14px",
                          boxShadow: active
                            ? `0 0 0 4px ${isDark
                              ? "rgba(245,158,11,0.2)"
                              : "rgba(245,158,11,0.2)"
                            }`
                            : "none",
                          zIndex: 1,
                        }}
                      >
                        {completed ? (
                          <CheckCircleIcon sx={{ fontSize: 20 }} />
                        ) : (
                          icon
                        )}
                      </Box>
                    );
                  }}
                >
                  <Typography
                    sx={{
                      color: activeStep >= index ? amber : textSecondary,
                      fontWeight: 700,
                      fontSize: "14px",
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography
                    sx={{ color: textSecondary, fontSize: "11px", mt: 0.5 }}
                  >
                    {step.desc}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box>
          {/* ── Step 1: Request Details ────────────────────────────── */}
          {activeStep === 0 && (
            <SectionCard title="REQUEST DETAILS">
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2.5,
                }}
              >
                {/* Date */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: textSecondary,
                      mb: 0.75,
                      letterSpacing: "0.3px",
                    }}
                  >
                    Date of Request <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    name="date"
                    type="date"
                    size="small"
                    value={meta.date}
                    onChange={handleMeta}
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx()}
                  />
                </Box>

                {/* Req No */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: textSecondary,
                      mb: 0.75,
                      letterSpacing: "0.3px",
                    }}
                  >
                    Requisition No.
                  </Typography>
                  <TextField
                    fullWidth
                    name="req_no"
                    size="small"
                    value={meta.req_no}
                    onChange={handleMeta}
                    placeholder="Auto-generated or manual"
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx()}
                  />
                </Box>

                {/* Department */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: textSecondary,
                      mb: 0.75,
                      letterSpacing: "0.3px",
                    }}
                  >
                    Department <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={departments}
                    getOptionLabel={(option) => option.name || ""}
                    value={
                      departments.find((d) => d.id === meta.department) || null
                    }
                    onChange={(e, newValue) => {
                      setMeta((prev) => ({
                        ...prev,
                        department: newValue ? newValue.id : "",
                      }));
                    }}
                    loading={departmentLoading}
                    disabled={departmentLoading}
                    filterOptions={(options, state) => {
                      const inputValue = state.inputValue.toLowerCase();
                      return options.filter(
                        (option) =>
                          option.name?.toLowerCase().includes(inputValue) ||
                          option.code?.toLowerCase().includes(inputValue),
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Department"
                        placeholder="Search by name or code"
                        error={!!errors.department}
                        helperText={errors.department}
                        sx={fieldSx(!!errors.department)}
                      />
                    )}
                  />
                </Box>

                {/* Requisition Description — full width */}
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: textSecondary,
                      mb: 0.75,
                      letterSpacing: "0.3px",
                    }}
                  >
                    Requisition Description{" "}
                    <span style={{ color: "#ef4444" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    name="description"
                    value={meta.description}
                    onChange={handleMeta}
                    error={!!errors.description}
                    helperText={
                      errors.description ||
                      "Briefly explain the reason for this requisition"
                    }
                    placeholder="Enter requisition description"
                    sx={fieldSx(!!errors.description)}
                  />
                </Box>

                {/* Purpose Toggle — full width */}
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: textSecondary,
                      mb: 0.75,
                      letterSpacing: "0.3px",
                    }}
                  >
                    Requisition Purpose
                  </Typography>
                  <Stack direction="row" spacing={1.5}>
                    {[
                      {
                        type: "COMPANY",
                        icon: <BusinessCenterIcon sx={{ fontSize: 16 }} />,
                        label: "Company",
                      },
                      {
                        type: "PROJECT",
                        icon: <FolderIcon sx={{ fontSize: 16 }} />,
                        label: "Project",
                      },
                    ].map(({ type, icon, label }) => (
                      <Button
                        key={type}
                        type="button"
                        onClick={() => setPurposeType(type)}
                        startIcon={icon}
                        variant={
                          purposeType === type ? "contained" : "outlined"
                        }
                        sx={{
                          borderRadius: "9px",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "13.5px",
                          px: 2.5,
                          py: 1,
                          ...(purposeType === type
                            ? {
                              background: `linear-gradient(135deg, ${amber} 0%, ${amberDark} 100%)`,
                              color: "#fff",
                              border: "none",
                              boxShadow: "0 4px 12px rgba(245,158,11,0.3)",
                              "&:hover": {
                                background: `linear-gradient(135deg, ${amberDark} 0%, ${amberDeep} 100%)`,
                              },
                            }
                            : {
                              borderColor: borderColor,
                              color: textSecondary,
                              "&:hover": {
                                borderColor: amber,
                                color: amber,
                                backgroundColor: isDark
                                  ? "rgba(245,158,11,0.08)"
                                  : "#fffbeb",
                              },
                            }),
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </Stack>
                </Box>

                {/* Project dropdown — shown only for "project" purpose */}
                {purposeType === "PROJECT" && (
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={[
                        ...projects,
                        { id: "OTHER", name: "Other / Custom Project" },
                      ]}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                      value={
                        projects.find(
                          (p) => String(p.id) === String(meta.project_id),
                        ) ||
                        (meta.project_id === "OTHER"
                          ? { id: "OTHER", name: "Other / Custom Project" }
                          : null)
                      }
                      onChange={(event, newValue) => {
                        setMeta((prev) => ({
                          ...prev,
                          project_id: newValue ? newValue.id : "",
                        }));
                        if (errors.project_id)
                          setErrors((prev) => ({ ...prev, project_id: "" }));
                      }}
                      loading={projectLoading}
                      disabled={projectLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Project"
                          error={!!errors.project_id}
                          helperText={errors.project_id}
                          InputLabelProps={{ shrink: true }}
                          sx={fieldSx(!!errors.project_id)}
                        />
                      )}
                    />

                    {meta.project_id === "OTHER" && (
                      <TextField
                        fullWidth
                        size="small"
                        name="custom_project"
                        value={meta.custom_project}
                        onChange={handleMeta}
                        placeholder="Enter custom project name"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          mt: 1.5,
                          ...fieldSx(),
                          "& .MuiOutlinedInput-root": {
                            ...fieldSx()["& .MuiOutlinedInput-root"],
                            backgroundColor: isDark
                              ? "rgba(245,158,11,0.08)"
                              : "#fffbeb",
                            "& fieldset": {
                              borderColor: amber,
                              borderStyle: "dashed",
                            },
                          },
                        }}
                      />
                    )}

                    {meta.project_id && meta.project_id !== "OTHER" && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: "10px",
                          backgroundColor: isDark
                            ? "rgba(255,255,255,0.03)"
                            : "#f8fafc",
                          border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: textPrimary,
                            mb: 1.5,
                          }}
                        >
                          Project Budget Overview
                        </Typography>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ mb: 1, width: "100%", gap: 2 }}
                        >
                          <Typography
                            sx={{ fontSize: "12px", color: textSecondary }}
                          >
                            Total Budget
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: textPrimary,
                            }}
                          >
                            {fmtINR(projectBudget)}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ mb: 1, width: "100%", gap: 2 }}
                        >
                          <Typography
                            sx={{ fontSize: "12px", color: textSecondary }}
                          >
                            Previous Spendings
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: textPrimary,
                            }}
                          >
                            {fmtINR(previousSpendings)}
                          </Typography>
                        </Stack>
                        <Divider
                          sx={{
                            my: 1,
                            borderColor: isDark ? "#334155" : "#e2e8f0",
                          }}
                        />
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ mb: 1, width: "100%", gap: 2 }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: isBudgetExceeded ? "#ef4444" : "#10b981",
                            }}
                          >
                            Remaining Budget
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: isBudgetExceeded ? "#ef4444" : "#10b981",
                            }}
                          >
                            {fmtINR(remainingBudget)}
                          </Typography>
                        </Stack>
                        {isBudgetExceeded && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#ef4444",
                              mt: 1,
                              fontStyle: "italic",
                            }}
                          >
                            Warning: Creating this requisition with high value
                            might exceed the remaining budget.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </SectionCard>
          )}

          {/* ── Step 2: Line Items ──────────────────────────────────── */}
          {activeStep === 1 && (
            <SectionCard title={null} sx={{ overflowX: "auto" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flex: 1 }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.7px",
                      color: textSecondary,
                    }}
                  >
                    Line Items
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: amber,
                      color: "#fff",
                      borderRadius: "50%",
                      fontSize: "11px",
                      fontWeight: 700,
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {totals.items}
                  </Box>
                </Stack>
                <Button
                  type="button"
                  onClick={addRow}
                  startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    borderColor: amber,
                    color: amber,
                    backgroundColor: isDark
                      ? "rgba(245,158,11,0.08)"
                      : "#fffbeb",
                    "&:hover": {
                      backgroundColor: amber,
                      color: "#fff",
                      borderColor: amber,
                    },
                  }}
                >
                  Add Item
                </Button>
              </Stack>

              {errors.rows && (
                <Typography
                  sx={{ color: "#ef4444", fontSize: "12px", mb: 1.5 }}
                >
                  {errors.rows}
                </Typography>
              )}

              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow
                      sx={{ background: isDark ? "#1e293b" : "#1a1f2e" }}
                    >
                      {[
                        "#",
                        "Item Name",
                        "Description",
                        "Qty",
                        "Rate (₹)",
                        "Amount (₹)",
                        "Required By",
                        "Actions",
                      ].map((h, i) => (
                        <TableCell
                          key={i}
                          align={
                            i === 0 || i === 7
                              ? "center"
                              : i >= 3 && i <= 5
                                ? "right"
                                : "left"
                          }
                          sx={{
                            color: "#fff",
                            fontSize: "11px",
                            fontWeight: 600,
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            py: 1.25,
                            px: 1.5,
                            whiteSpace: "nowrap",
                            borderBottom: "none",
                            "&:first-of-type": {
                              borderRadius: "7px 0 0 7px",
                              width: 36,
                            },
                            "&:last-of-type": {
                              borderRadius: "0 7px 7px 0",
                              width: 80,
                            },
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="center"
                          sx={{ py: 6, border: "none" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontSize: "28px" }}>
                              📋
                            </Typography>
                            <Typography
                              sx={{ fontSize: "14px", color: textSecondary }}
                            >
                              No items added yet. Click{" "}
                              <span
                                style={{
                                  color: amber,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                                onClick={addRow}
                              >
                                + Add Item
                              </span>{" "}
                              to begin.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, idx) => (
                        <TableRowItem
                          key={`row-${row.id}`}
                          row={row}
                          idx={idx}
                          cardBorder={cardBorder}
                          isDark={isDark}
                          textSecondary={textSecondary}
                          textPrimary={textPrimary}
                          onRowUpdate={handleRowUpdate}
                          deleteRow={deleteRow}
                          tblInputSx={tblInputSx}
                          masterItems={masterItems}
                          masterItemsLoading={masterItemsLoading}
                          canDeleteRow={canDeleteRow}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>

              {/* Totals bar */}
              <Stack
                direction="row"
                sx={{ mt: 2.5, pt: 2.5, borderTop: `2px solid ${cardBorder}` }}
              >
                <Box
                  sx={{
                    background: isDark ? "#01162b" : "#1a1f2e",
                    borderRadius: "10px",
                    px: 3.5,
                    py: 2.25,
                    display: "grid",
                    gridTemplateColumns: "auto auto",
                    gap: "8px 40px",
                    minWidth: 300,
                    marginLeft: "auto",
                  }}
                >
                  {[
                    { label: "No. of Items", value: totals.items },
                    { label: "Total Qty", value: totals.qty },
                  ].map(({ label, value }) => (
                    <React.Fragment key={label}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 500,
                          color: "rgba(255,255,255,0.55)",
                          alignSelf: "center",
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#fff",
                          textAlign: "right",
                        }}
                      >
                        {value}
                      </Typography>
                    </React.Fragment>
                  ))}
                  <Divider
                    sx={{
                      gridColumn: "1/-1",
                      borderColor: "rgba(255,255,255,0.15)",
                      my: 0.5,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#fff",
                      alignSelf: "center",
                    }}
                  >
                    Grand Total
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "20px",
                      fontWeight: 700,
                      color: isBudgetExceeded ? "#ef4444" : "#7dd3fc",
                      textAlign: "right",
                    }}
                  >
                    {fmtINR(totals.amount)}
                  </Typography>
                </Box>
              </Stack>
            </SectionCard>
          )}

          {/* ── Step 3: Review & Submit ────────────────────────────── */}
          {activeStep === 2 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
                gap: 3,
              }}
            >
              <SectionCard
                title="REQUEST DETAILS"
                sx={{ height: "100%", mb: 0 }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 3,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontSize: "11px", color: textSecondary, mb: 0.5 }}
                    >
                      Requested By
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: textPrimary,
                      }}
                    >
                      {currentUser?.first_name
                        ? `${currentUser.first_name} ${currentUser.last_name
                        } (${currentUser.employee_code || ""})`
                        : "Employee"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: "11px", color: textSecondary, mb: 0.5 }}
                    >
                      Requisition No.
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: textPrimary,
                      }}
                    >
                      {meta.req_no}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: "11px", color: textSecondary, mb: 0.5 }}
                    >
                      Department
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: textPrimary,
                      }}
                    >
                      {departments.find(
                        (d) => String(d.id) === String(meta.department),
                      )?.name || "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: "11px", color: textSecondary, mb: 0.5 }}
                    >
                      Requisition Purpose
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "6px",
                        backgroundColor: amber,
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {purposeType === "COMPANY" ? "Company" : "Project"}
                    </Box>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: "11px", color: textSecondary, mb: 0.5 }}
                    >
                      Date of Request
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: textPrimary,
                      }}
                    >
                      {new Date(meta.date)
                        .toLocaleDateString("en-GB")
                        .replace(/\//g, "-")}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: "11px", color: textSecondary, mb: 0.5 }}
                    >
                      Description
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: textPrimary,
                      }}
                    >
                      {meta.description}
                    </Typography>
                  </Box>
                </Box>
              </SectionCard>

              <SectionCard
                title="SUMMARY"
                sx={{
                  height: "100%",
                  mb: 0,
                  backgroundColor: isDark ? "#1e293b" : "#1a1f2e",
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      sx={{ fontSize: "13px", color: "rgba(255,255,255,0.7)" }}
                    >
                      Total Items
                    </Typography>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}
                    >
                      {totals.items}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      sx={{ fontSize: "13px", color: "rgba(255,255,255,0.7)" }}
                    >
                      Total Quantity
                    </Typography>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}
                    >
                      {totals.qty}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      sx={{ fontSize: "13px", color: "rgba(255,255,255,0.7)" }}
                    >
                      Total Amount (₹)
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#fff",
                        fontFamily: "monospace",
                      }}
                    >
                      {Number(totals.amount).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </Stack>

                  <Divider
                    sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1 }}
                  />

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      sx={{ fontSize: "15px", fontWeight: 700, color: "#fff" }}
                    >
                      Grand Total
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "22px",
                        fontWeight: 700,
                        color: "#7dd3fc",
                        fontFamily: "monospace",
                      }}
                    >
                      {fmtINR(totals.amount)}
                    </Typography>
                  </Stack>
                </Stack>
              </SectionCard>

              <SectionCard
                title={`LINE ITEMS (${totals.items})`}
                sx={{ gridColumn: "1 / -1", mt: 3, mb: 3, overflowX: "auto" }}
              >
                <Table size="small" sx={{ minWidth: 640 }}>
                  <TableHead>
                    <TableRow
                      sx={{ background: isDark ? "#1e293b" : "#f1f5f9" }}
                    >
                      {[
                        "#",
                        "Item Name",
                        "Description",
                        "Qty",
                        "Rate (₹)",
                        "Amount (₹)",
                        "Required By",
                      ].map((h, i) => (
                        <TableCell
                          key={i}
                          align={
                            i === 0
                              ? "center"
                              : i >= 3 && i <= 5
                                ? "right"
                                : "left"
                          }
                          sx={{
                            color: isDark ? "#fff" : "#475569",
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            py: 1.25,
                            px: 1.5,
                            whiteSpace: "nowrap",
                            borderBottom: "none",
                            "&:first-of-type": {
                              borderRadius: "7px 0 0 7px",
                              width: 36,
                            },
                            "&:last-of-type": { borderRadius: "0 7px 7px 0" },
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => {
                      if (!row.itemName || !row.description) return null;
                      const amt =
                        (parseFloat(row.qty) || 0) *
                        (parseFloat(row.rate) || 0);
                      return (
                        <TableRow
                          key={`ro-row-${row.id}`}
                          sx={{
                            borderBottom: `1px solid ${cardBorder}`,
                            "&:last-child": { borderBottom: "none" },
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{
                              color: textSecondary,
                              fontSize: "12px",
                              fontWeight: 600,
                              borderBottom: "none",
                            }}
                          >
                            {idx + 1}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: textPrimary,
                              fontSize: "13px",
                              borderBottom: "none",
                            }}
                          >
                            {row.itemName}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: textPrimary,
                              fontSize: "13px",
                              borderBottom: "none",
                            }}
                          >
                            {row.description}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: textPrimary,
                              fontSize: "13px",
                              borderBottom: "none",
                            }}
                          >
                            {row.qty}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: textPrimary,
                              fontSize: "13px",
                              borderBottom: "none",
                            }}
                          >
                            {Number(row.rate || 0).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: textPrimary,
                              fontSize: "13px",
                              fontWeight: 600,
                              borderBottom: "none",
                            }}
                          >
                            {Number(amt).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: textPrimary,
                              fontSize: "13px",
                              borderBottom: "none",
                            }}
                          >
                            {row.requiredBy
                              ? new Date(row.requiredBy)
                                .toLocaleDateString("en-GB")
                                .replace(/\//g, "-")
                              : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </SectionCard>
            </Box>
          )}

          {/* ── Action Buttons ─────────────────────────────────────────── */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={activeStep === 0 ? "flex-end" : "space-between"}
            spacing={2}
            sx={{ mt: 1, mb: 4 }}
          >
            {activeStep > 0 && (
              <Button
                type="button"
                onClick={handleBack}
                disabled={loading}
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  px: 4,
                  py: 1.3,
                  textTransform: "none",
                  fontWeight: 500,
                  borderColor: isDark ? "#475569" : "#d1d5db",
                  color: isDark ? "#d1d5db" : "#374151",
                  "&:hover": {
                    borderColor: amber,
                    backgroundColor: isDark
                      ? "rgba(245,158,11,0.08)"
                      : "#fffbeb",
                  },
                }}
              >
                &lt; Back: {activeStep === 1 ? "Request Details" : "Line Items"}
              </Button>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ width: activeStep === 0 ? "100%" : "auto" }}
            >
              <Button
                type="button"
                onClick={resetForm}
                disabled={loading}
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  px: 4,
                  py: 1.3,
                  textTransform: "none",
                  fontWeight: 500,
                  borderColor: isDark ? "#475569" : "#d1d5db",
                  color: isDark ? "#d1d5db" : "#374151",
                  "&:hover": {
                    borderColor: amber,
                    backgroundColor: isDark
                      ? "rgba(245,158,11,0.08)"
                      : "#fffbeb",
                  },
                }}
              >
                Cancel
              </Button>

              {activeStep < steps.length - 1 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  sx={{
                    borderRadius: "12px",
                    px: 4,
                    py: 1.3,
                    textTransform: "none",
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${amber} 0%, ${amberDark} 100%)`,
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245,158,11,0.25)",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${amberDark} 0%, ${amberDeep} 100%)`,
                      transform: "scale(1.01)",
                      boxShadow: "0 20px 25px -5px rgba(245,158,11,0.3)",
                    },
                  }}
                >
                  Next: {steps[activeStep + 1].label} &gt;
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    borderRadius: "12px",
                    px: 4,
                    py: 1.3,
                    textTransform: "none",
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${amber} 0%, ${amberDark} 100%)`,
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245,158,11,0.25)",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${amberDark} 0%, ${amberDeep} 100%)`,
                      transform: "scale(1.01)",
                      boxShadow: "0 20px 25px -5px rgba(245,158,11,0.3)",
                    },
                    "&:disabled": { opacity: 0.7 },
                    transition: "all 0.2s",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Submit Requisition"
                  )}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateRequisition;